/**
 * SkillLevelChip.tsx
 * Renders a skill level badge (1–5) as a flat 28×28 rounded-square <Box>.
 *
 * Level semantics (BUG-2026-0124 — scale expanded 1–4 → 1–5; full definitions
 * live in constants/scheduleEnums.ts SKILL_LEVEL_META):
 *   1 = Has not begun training      — red        (theme.schedule.skillLevel1)
 *   2 = Training ongoing            — orange     (theme.schedule.skillLevel2)
 *   3 = Proficient, can train       — yellow     (theme.schedule.skillLevel3)
 *   4 = Reviewer (can't train)      — green      (theme.schedule.skillLevel4)
 *   5 = Trainer (evaluates others)  — dark green (theme.schedule.skillLevel5)
 *
 * null / undefined:
 *   - Default: renders nothing (return null). Empty grid cells stay clean.
 *   - showEmpty=true: renders a dashed-ring placeholder (used in legend).
 *   - showEmpty=true + focused=true: the dashed ring switches from grey
 *     (theme.palette.divider) to sage primary (theme.palette.primary.main)
 *     and thickens to 1.5px. This is the sole focus indicator — no
 *     box-shadow or border is applied to the parent cell.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { SKILL_LEVELS, SKILL_LEVEL_META } from '../../constants/scheduleEnums';

/**
 * Concise per-level labels. Derived from the canonical SKILL_LEVEL_META so the
 * scale (1–5) is defined in exactly one place (BUG-2026-0124).
 */
export const SKILL_LEVEL_LABELS: Record<number, string> = Object.fromEntries(
  SKILL_LEVELS.map((lvl) => [lvl, SKILL_LEVEL_META[lvl].label]),
);

/**
 * Full BGC definitions per level (used in legend rows / tooltips).
 */
export const SKILL_LEVEL_DESCRIPTIONS: Record<number, string> = Object.fromEntries(
  SKILL_LEVELS.map((lvl) => [lvl, SKILL_LEVEL_META[lvl].description]),
);

/** Kept for back-compat — callers (tooltips, popovers) may reference these. */
export const SKILL_LEVEL_SHORT: Record<number, string> = Object.fromEntries(
  SKILL_LEVELS.map((lvl) => [lvl, SKILL_LEVEL_META[lvl].short]),
);

const BADGE_SIZE = 28;
const BORDER_RADIUS = 6;

interface SkillLevelChipProps {
  level: number | null | undefined;
  /** Ignored — kept for API compatibility with callers that pass size="small". */
  size?: string;
  /**
   * Explicit pixel size for the badge square.
   * When provided, overrides the default BADGE_SIZE (28px).
   * Border-radius scales proportionally to preserve the rounded-square look.
   */
  pixelSize?: number;
  /** When true, shows badge + full description inline (for legend rows). */
  showLabel?: boolean;
  /** When true and level is null, renders a dashed placeholder ring. */
  showEmpty?: boolean;
  /**
   * When true, the empty placeholder ring changes from grey (divider) to
   * sage primary at 1.5px — signalling keyboard focus without a box-shadow
   * rectangle around the parent cell.
   * Only meaningful when showEmpty=true and level is null.
   */
  focused?: boolean;
}

export const SkillLevelChip: React.FC<SkillLevelChipProps> = ({
  level,
  pixelSize,
  showLabel = false,
  showEmpty = false,
  focused = false,
}) => {
  const theme = useTheme();

  // Resolve effective size — pixelSize prop overrides the default.
  const effectiveSize = pixelSize ?? BADGE_SIZE;
  // Scale border-radius proportionally to the badge size.
  const effectiveRadius = Math.round((BORDER_RADIUS / BADGE_SIZE) * effectiveSize);

  if (level == null) {
    if (!showEmpty) return null;
    return (
      <Box
        data-testid="skill-badge-empty"
        sx={{
          width: effectiveSize,
          height: effectiveSize,
          borderRadius: `${effectiveRadius}px`,
          border: focused
            ? `1.5px dashed ${theme.palette.primary.main}`
            : `1px dashed ${theme.palette.divider}`,
          flexShrink: 0,
          transition: 'border-color 0.1s ease, border-width 0.1s ease',
        }}
      />
    );
  }

  const colorMap: Record<number, string> = {
    1: theme.schedule.skillLevel1,
    2: theme.schedule.skillLevel2,
    3: theme.schedule.skillLevel3,
    4: theme.schedule.skillLevel4,
    5: theme.schedule.skillLevel5,
    9: theme.schedule.skillLevel9,
  };

  const bg = colorMap[level] ?? theme.schedule.noActivity;

  const badge = (
    <Box
      data-testid={`skill-badge-${level}`}
      sx={{
        width: effectiveSize,
        height: effectiveSize,
        borderRadius: `${effectiveRadius}px`,
        backgroundColor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'transform 80ms ease, filter 80ms ease',
        '&:hover': {
          transform: 'scale(1.05)',
          filter: 'brightness(1.1)',
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          color: 'common.white',
          fontWeight: 700,
          fontSize: '0.75rem',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        {level}
      </Typography>
    </Box>
  );

  if (!showLabel) return badge;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {badge}
      <Typography variant="caption" color="text.secondary">
        {SKILL_LEVEL_LABELS[level] ?? `Level ${level}`}
      </Typography>
    </Box>
  );
};
